import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React from "react";
import { Button, Icon, useInlineRowActionChrome } from "@salestrak/ui";

import type { IssueRow } from "#app/lib/data";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { EntityGroupTag } from "#app/components/entity";
import { TeamsPickerContent } from "#app/components/teams/teams-picker";
import type { FieldLabelMode, FieldLayout, TextLabelLayout } from "./issue-card-fields";
import {
  IssueCardPropertyToken,
  SelectorPopover,
} from "./issue-card-property-chip";

// ---------------------------------------------------------------------------
// Teams property on the card — two states, mirroring address:
//   VALUED → a body-row token above the footer (the square-tile stack —
//   squares are teams, circles are people).
//   EMPTY  → an icon-only footer action right of the address action.
// Both open the same team picker; the write is board-level (onTeamsChange),
// never per card.
// ---------------------------------------------------------------------------

function teamsPickerContent(
  issue: IssueRow,
  onTeamsChange: (issue: IssueRow, teamIds: string[]) => void,
) {
  return () => (
    <TeamsPickerContent
      value={(issue.teams ?? []).map((t) => t.teamId)}
      onValueChange={(ids) => onTeamsChange(issue, ids)}
      selectedTeams={issue.teams ?? []}
    />
  );
}

/** Body-row token for a card whose Teams property has a value. */
export function IssueCardTeams({
  issue,
  canApply,
  onTeamsChange,
  labelMode = "icon",
  labelLayout = "horizontal",
  layout = "wrap",
  labelRail,
}: {
  issue: IssueRow;
  /** Viewer may edit (card edit access) — gates the selector popover. */
  canApply: boolean;
  onTeamsChange: (issue: IssueRow, teamIds: string[]) => void;
  labelMode?: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout?: FieldLayout;
  labelRail?: number;
}) {
  const teams = issue.teams ?? [];
  if (!teams.length) return null;
  const entities = teams.map((t) => ({
    kind: "team" as const,
    id: t.teamId,
    team: t,
  }));

  // A card token is the tile stack whatever the count — one team is a lone
  // tile, never tile + name. Each tile hovers to its team preview; the token
  // owns the press.
  const chip = (
    <IssueCardPropertyToken
      label="Teams"
      icon={PROPERTY_ICON.team}
      // Teams KEEP the rail glyph (Spencer, 2026-09-09): a team tile is a
      // colored shape carrying an identity, not a self-describing kind mark —
      // unlike a stage pie, the tile alone does not say "teams".
      canApply={canApply}
      labelMode={labelMode}
      labelLayout={labelLayout}
      layout={layout}
      labelRail={labelRail}
    >
      <EntityGroupTag
        entities={entities}
        size="xs"
        title="Teams"
        surface="card"
        interactive={false}
      />
    </IssueCardPropertyToken>
  );

  if (!canApply) return chip;
  return (
    <SelectorPopover chip={chip} content={teamsPickerContent(issue, onTeamsChange)} sheetTitle="Teams" />
  );
}

/** Icon-only footer action for a card with no team assigned — the same
 *  empty-property pattern as the address footer action. Its glyph is the
 *  OUTLINE kind mark like the footer's other actions, never the part-filled
 *  property label glyph: solid is what a valued tile looks like. */
export function IssueCardTeamsAction({
  issue,
  onTeamsChange,
}: {
  issue: IssueRow;
  onTeamsChange: (issue: IssueRow, teamIds: string[]) => void;
}) {
  // Rests in the card footer's capsule: a ghost on the capsule's hairline.
  const chrome = useInlineRowActionChrome();
  return (
    <SelectorPopover
      nativeButton
      sheetTitle="Teams"
      chip={
        <Button
          variant={chrome.variant}
          className={chrome.className}
          size="icon-xs"
          shape="square"
          aria-label="Add teams"
          // The card press opens the issue — the trigger press must not.
          onClick={(e) => e.stopPropagation()}
        >
          <Icon name={ENTITY_KIND_ICON.team} size="md" color="muted" />
        </Button>
      }
      content={teamsPickerContent(issue, onTeamsChange)}
    />
  );
}
